import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { MODALS } from 'src/models/ModalModel';
import { MouseEvent, useEffect, useState } from 'react';
import {
  getFundraisingTransactionAction,
  putFundraisingTransactionAction,
} from 'src/redux/actions/FundraisingTransactionAction';
import Header from 'src/components/UI/Header';
import TopRow from 'src/components/UI/TopRow';
import Title from 'src/components/UI/Title';
import CloseButton from 'src/components/UI/CloseButton';
import { closeModal, openModal } from 'src/redux/actions/modalActions';
import AmbassadorSelector from 'src/components/Control/AmbassadorSelector';
import { FundraisingTransaction } from 'src/models/FundraisingModel';
import ContactSelector from 'src/components/Control/ContactSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment-timezone-all';
import FormDateTimePicker from 'src/components/Control/FormControls/FormDateTimePicker';
import Scroller from 'src/components/UI/Scroller';
import Row from 'src/components/UI/Row';
import { getInvoiceAction } from 'src/redux/actions/invoiceActions';
import { getPaymentAction } from 'src/redux/actions/paymentActions';
import { getAmbassadorByIdAction } from 'src/redux/actions/ambassadorActions';
import { getContactByIdAction } from 'src/redux/actions/contactActions';
import { getFundraisingAction } from 'src/redux/actions/fundraisingAction';
import TextLink from 'src/components/UI/TextLink';
import { setSelections } from 'src/redux/actions/selectionActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import Button from '@material-ui/core/Button';
import BottomRow from 'src/components/UI/BottomRow';
import { setError } from 'src/redux/actions/errorsActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import FundraisingTransactionValidator from 'src/validations/FundraisingTransactionValidator';

const FundraisingEditTransactionForm = () => {
  const dispatch = useAppDispatch();
  const { transactionId, fundraisingId } = useSelector(
    (state: Store) => state.modals[MODALS.editTransaction].payload,
  );

  const transaction = useSelector(
    (state: Store) => state.fundraisingTransaction[transactionId] as FundraisingTransaction,
  );

  const fundraising = useSelector(
    (state: Store) => state.fundraisings[fundraisingId],
  );

  const [transactionState, setTransactionState] =
    useState<FundraisingTransaction>(transaction || {} as FundraisingTransaction);

  useEffect(() => {
    dispatch(getFundraisingTransactionAction(transactionId));
    dispatch(getFundraisingAction(fundraisingId));
  }, [transactionId]);

  useEffect(() => {
    if (transaction) {
      dispatch(getInvoiceAction(transaction.invoice_id));
      dispatch(getPaymentAction(transaction.payment_id));
      dispatch(getAmbassadorByIdAction(transaction.ambassador_id));
      dispatch(getContactByIdAction(transaction.contact_id));
      setTransactionState(transaction);
    }
  }, [transaction]);

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.editTransaction }));
  };

  const handleShowInvoice = () => {
    if (!transaction) { return; }
    dispatch(setSelections({
      entity: ENTITIES.invoices,
      selections: [transaction.invoice_id],
    }));
    dispatch(openModal({ modal: MODALS.editInvoice }));
  };

  const handleShowPayment = () => {
    if (!transaction) { return; }
    dispatch(setSelections({
      entity: ENTITIES.payments,
      selections: [transaction.payment_id],
    }));
    dispatch(openModal({ modal: MODALS.editPayment }));
  };

  const onChange = (value: Value, field: string) => {
    setTransactionState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.fundraisingTransaction]: {} }));
    try {
      FundraisingTransactionValidator().validateSync(transactionState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving transaction',
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.fundraisingTransaction]: errorBag }));
      return false;
    }

    dispatch(putFundraisingTransactionAction(transactionState));
    return true;
  };

  return (
    <>
      <div style={{ minHeight: '720px' }}>
        <Header height="48px">
          <TopRow>
            <Title>
              Edit transaction #{transactionState?.id}
            </Title>
            <div style={{ marginLeft: 'auto' }}>
              <CloseButton handleClose={handleClose} />
            </div>
          </TopRow>
        </Header>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
        }}>
          { transaction?.invoice_id && (
            <TextLink
              id="show-invoice-link"
              onClick={handleShowInvoice}>
              show invoice
            </TextLink>
          )}
          { transaction?.payment_id && (
            <TextLink
              id="manage-departments-link"
              onClick={handleShowPayment}>
              show payment
            </TextLink>
          )}
        </div>
        <Scroller>
          <FormTextField
            form={FORM.fundraisingTransaction}
            label="Title"
            name="title"
            onChange={onChange}
            required
            value={transactionState?.title || ''} />
          <Row>
            <div style={{ width: '50%' }}>
              <AmbassadorSelector
                fundraisingId={fundraisingId}
                fundraisingSlug={fundraising?.fundraisingInfo?.slug || ''}
                form={FORM.fundraisingTransaction}
                label="Ambassador"
                name="ambassador_id"
                value={transactionState?.ambassador_id || ''}
                onChange={(id: string | number) => onChange(id, 'ambassador_id')}
                showEdit />
            </div>
            <div style={{ width: '50%' }}>
              <ContactSelector
                autocompleteId={AUTOCOMPLETE_ID.add_fundraising_contact}
                form={FORM.fundraisingTransaction}
                name="contact_id"
                label="Contact"
                onChange={(id: number) => onChange(id, 'contact_id')}
                contactId={transactionState?.contact_id}
                hideAdd />
            </div>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <div style={{ width: '50%' }}>
              <FormTextField
                form={FORM.fundraisingTransaction}
                label="Donor Name Displayed"
                name="donor_name_displayed"
                onChange={onChange}
                required
                value={transactionState?.donor_name_displayed || ''} />
            </div>
            <div style={{ width: '50%' }}>
              <FormCheckbox
                label="Anonymous"
                name="is_anonymous"
                onChange={onChange}
                checked={!!transactionState?.is_anonymous} />
            </div>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <div style={{ width: '50%', display: 'flex' }}>
              <div style={{ width: '50%', paddingRight: '16px' }}>
                <FormTextField
                  form={FORM.fundraisingTransaction}
                  label="Amount Multiplier"
                  name="amount_multiplier"
                  onChange={onChange}
                  required
                  value={transactionState?.amount_multiplier || ''} />
              </div>
              <div style={{ width: '50%' }}>
                <FormDateTimePicker
                  form={FORM.fundraisingInfo}
                  label="Pledge Date"
                  name="pledge_date"
                  onChange={
                  (value: MaterialUiPickersDate) => onChange(value, 'pledge_date')
                }
                  value={transactionState?.pledge_date || moment()}
                  showTime
                  required />
              </div>
            </div>
            <div style={{ width: '50%' }}>
              <FormCheckbox
                style={{ marginTop: '0px' }}
                label="Show pledge"
                name="show_pledge"
                onChange={onChange}
                checked={!!transactionState?.show_pledge} />
            </div>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <div style={{ width: '50%' }}>
              <FormTextField
                form={FORM.fundraisingTransaction}
                label="Source Reference"
                name="source_reference"
                onChange={onChange}
                required
                value={transactionState?.source_reference || ''} />
            </div>
            <div style={{ width: '50%' }}>
              <FormTextField
                form={FORM.fundraisingTransaction}
                label="Source Reference URL"
                name="source_reference_url"
                onChange={onChange}
                required
                value={transactionState?.source_reference_url || ''} />
            </div>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <FormTextField
              form={FORM.fundraisingTransaction}
              label="Donor Comment"
              name="donor_comment"
              onChange={onChange}
              multiline
              minRows={3}
              maxRows={5}
              required
              value={transactionState?.donor_comment || ''} />
          </Row>
          <BottomRow style={{ marginTop: '-16px' }}>
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: '12px' }}
              onClick={handleSubmit}
              key="submit-button">
              save
            </Button>
          </BottomRow>
        </Scroller>
      </div>
    </>
  );
};

export default FundraisingEditTransactionForm;
